import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { pushDepositPaid } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DepositConfirmed() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking"); // checking | paid | processing

  useEffect(() => {
    if (!orderId) {
      setStatus("processing");
      return undefined;
    }
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/deposits/${orderId}`);
        if (data.status === "PAID") {
          pushDepositPaid(orderId);
          setStatus("paid");
          clearInterval(timer);
          return;
        }
      } catch {
        /* keep polling */
      }
      if (tries >= 20) {
        setStatus("processing");
        clearInterval(timer);
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [orderId]);

  return (
    <main
      data-testid="deposit-confirmed-page"
      className="flex min-h-screen flex-col items-center justify-center bg-[var(--brand-bg)] px-6 text-center"
    >
      <Seo
        title="Deposit Confirmed | All-In Precision Wellness"
        description="Your appointment deposit has been received."
        path="/deposit-confirmed"
        noindex
      />
      <img src="/images/logo.png" alt="All-In Precision Wellness" className="mb-8 h-16 w-16 object-contain" />
      {status === "checking" ? (
        <>
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-[var(--brand-gold)]" aria-hidden="true" />
          <h1 className="font-serif-brand text-2xl sm:text-3xl font-semibold text-[var(--brand-navy)]">
            Confirming your deposit&hellip;
          </h1>
          <p className="mt-3 max-w-md text-sm sm:text-base text-[var(--brand-muted)]">
            One moment while we verify your payment.
          </p>
        </>
      ) : (
        <>
          <CheckCircle2 className="mb-4 h-10 w-10 text-emerald-600" aria-hidden="true" />
          <h1 data-testid="deposit-confirmed-heading" className="font-serif-brand text-2xl sm:text-3xl font-semibold text-[var(--brand-navy)]">
            {status === "paid" ? "Your $25 deposit is confirmed" : "Payment received"}
          </h1>
          <p className="mt-3 max-w-md text-sm sm:text-base text-[var(--brand-muted)]">
            {status === "paid"
              ? "Thank you — your deposit has been applied toward your visit. We look forward to seeing you."
              : "Thank you — your payment is being finalized. You'll see it applied toward your visit, and the office will reach out if anything else is needed."}
          </p>
        </>
      )}
      <Link
        data-testid="deposit-back-home"
        to="/menopause"
        className="mt-8 inline-flex items-center bg-[var(--brand-navy)] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--brand-navy-deep)]"
      >
        Back to All-In Precision Wellness
      </Link>
    </main>
  );
}
